import { Trans, useTranslation } from 'react-i18next';
import { Plus, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ToggleRow } from '~/components/toggle-row';
import { Field } from '~/components/promotion/editor-section';
import { EntityMultiSelect } from '~/components/promotion/entity-multi-select';
import { toGreekCaps } from '~/lib/i18n/toGreekCaps';
import { MOCK_CATEGORIES, loadMockProducts } from '~/lib/lookups/mock';
import type { LookupOption } from '~/lib/lookups/mock';
import { cn } from '@/lib/utils';
import { bogoPercentageOptional } from '~/lib/schemas/actions';
import type { BogoOfferTarget, BogoReachPricingMode, PromotionAction } from '~/lib/schemas/actions';
import { DiscountModeInput } from './discount-mode-input';

type Action = Extract<PromotionAction, { type: 'bogo' }>;

interface Props {
	value: Action;
	onChange: (next: Action) => void;
}

const PILL =
	'mx-1 w-12 rounded border border-brand-border-subtle bg-brand-subtle px-2 py-1 text-center font-semibold text-brand-subtle-fg focus-visible:outline-none';
const HEAD = 'text-[10px] font-semibold tracking-[0.04em] text-foreground-subtle';

/** `''` → null so optional numeric inputs can be cleared. */
const optionalNum = (raw: string): number | null => (raw === '' ? null : Number.isFinite(Number(raw)) ? Number(raw) : null);

/** Inline numeric input styled as a teal-tinted pill inside the fluent expression. Empty → null when `nullable`. */
function InlinePill({
	value,
	onChange,
	min = 0,
	nullable = false
}: {
	value: number | null;
	onChange: (n: number | null) => void;
	min?: number;
	nullable?: boolean;
}) {
	return (
		<input
			type="number"
			min={min}
			value={value != null && Number.isFinite(value) ? value : ''}
			onChange={e => onChange(e.target.value === '' ? (nullable ? null : 0) : Number(e.target.value))}
			className={cn(PILL)}
		/>
	);
}

interface OfferTargetRowsProps {
	title: string;
	pickerLabel: string;
	addLabel: string;
	rows: BogoOfferTarget[];
	onChange: (rows: BogoOfferTarget[]) => void;
	/** MaxQuantity is meaningful on product rows only (BW-8259). */
	showMaxQty: boolean;
	options: LookupOption[];
	loadOptions?: (query: string) => Promise<LookupOption[]>;
	percentagePlaceholder: string;
}

/** BW-8222 offer-target rows: id + optional own discount (% / €) + (products only) max qty. */
function OfferTargetRows({
	title,
	pickerLabel,
	addLabel,
	rows,
	onChange,
	showMaxQty,
	options,
	loadOptions,
	percentagePlaceholder
}: OfferTargetRowsProps) {
	const { t } = useTranslation();
	const cols = showMaxQty ? 'grid-cols-[1fr_90px_72px_70px_32px]' : 'grid-cols-[1fr_90px_72px_32px]';
	const setRow = (index: number, patch: Partial<BogoOfferTarget>) => onChange(rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));

	return (
		<div className="flex flex-col gap-2">
			<span className="text-sm font-medium text-foreground">{title}</span>
			{rows.length > 0 && (
				<div className={cn('grid items-center gap-2 px-1', cols)}>
					<span className={HEAD}>{toGreekCaps(pickerLabel)}</span>
					<span className={HEAD}>{toGreekCaps(t('promotion.action.bogo.ownDiscount'))}</span>
					<span className={HEAD}>{toGreekCaps(t('promotion.action.discountMode.label'))}</span>
					{showMaxQty && <span className={HEAD}>{toGreekCaps(t('promotion.action.perProduct.maxQty'))}</span>}
					<span />
				</div>
			)}
			{rows.map((row, index) => (
				<div key={index} className={cn('grid items-center gap-2', cols)}>
					<EntityMultiSelect
						value={row.id ? [row.id] : []}
						onChange={ids => setRow(index, { id: ids[ids.length - 1] ?? 0 })}
						options={options}
						loadOptions={loadOptions}
						placeholder={t('common.search')}
					/>
					<Input
						type="number"
						min={0}
						max={row.discountMode === 'percentage' ? 100 : undefined}
						placeholder={percentagePlaceholder}
						value={row.discount ?? ''}
						onChange={e => setRow(index, { discount: optionalNum(e.target.value) })}
					/>
					<DiscountModeInput
						value={row.discountMode}
						onChange={discountMode => setRow(index, { discountMode })}
						disabled={row.discount == null}
					/>
					{showMaxQty && (
						<Input
							type="number"
							min={1}
							placeholder="∞"
							value={row.maxQty ?? ''}
							onChange={e => setRow(index, { maxQty: optionalNum(e.target.value) })}
						/>
					)}
					<button
						type="button"
						onClick={() => onChange(rows.filter((_, i) => i !== index))}
						className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-muted hover:bg-muted hover:text-foreground"
						aria-label="remove row">
						<X size={14} />
					</button>
				</div>
			))}
			<button
				type="button"
				onClick={() => onChange([...rows, { id: 0, discount: null, discountMode: 'percentage', maxQty: null }])}
				className="flex items-center justify-center gap-1.5 rounded-md border border-dashed border-border-strong py-2 text-sm text-foreground-muted transition-colors hover:border-brand hover:bg-brand-subtle hover:text-brand-subtle-fg">
				<Plus size={14} /> {addLabel}
			</button>
		</div>
	);
}

export function BogoBody({ value, onChange }: Props) {
	const { t } = useTranslation();
	const percentageOptional = bogoPercentageOptional(value.offerProducts, value.offerCategories);
	const percentageMissing = value.z == null && !percentageOptional;
	const percentagePlaceholder = value.z != null ? `${value.z}%` : '—';

	return (
		<div className="flex flex-col gap-4">
			<div className="flex flex-col gap-1">
				<p className="flex flex-wrap items-center text-sm text-foreground">
					<Trans
						i18nKey="promotion.action.bogo.expression"
						components={[
							<InlinePill key="x" value={value.x} min={1} onChange={x => onChange({ ...value, x: x ?? 0 })} />,
							<InlinePill key="y" value={value.y} min={1} onChange={y => onChange({ ...value, y: y ?? 0 })} />,
							<InlinePill key="z" value={value.z} nullable onChange={z => onChange({ ...value, z })} />
						]}
					/>
				</p>
				{percentageMissing ? (
					<p className="text-xs text-danger-fg">{t('promotion.action.bogo.percentageRequired')}</p>
				) : percentageOptional ? (
					<p className="text-xs text-foreground-muted">{t('promotion.action.bogo.percentageOptional')}</p>
				) : null}
			</div>
			<ToggleRow
				label={t('promotion.action.bogo.sameProductOnly')}
				help={t('promotion.action.bogo.sameProductOnlyHelp')}
				checked={value.sameProductOnly}
				onCheckedChange={sameProductOnly => onChange({ ...value, sameProductOnly })}
			/>
			<Field
				label={t('promotion.action.bogo.reachPricingMode')}
				help={t('promotion.action.bogo.reachPricingModeHelp')}
				className="max-w-md">
				<Select value={value.reachPricingMode} onValueChange={v => onChange({ ...value, reachPricingMode: v as BogoReachPricingMode })}>
					<SelectTrigger>
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="afterDiscount">{t('promotion.action.bogo.reachPricingAfterDiscount')}</SelectItem>
						<SelectItem value="beforeDiscount">{t('promotion.action.bogo.reachPricingBeforeDiscount')}</SelectItem>
					</SelectContent>
				</Select>
			</Field>
			<div className="flex flex-col gap-3 rounded-lg border border-border bg-elevated p-3">
				<div>
					<p className="text-sm font-semibold text-foreground">{t('promotion.action.bogo.offerTargets')}</p>
					<p className="mt-0.5 text-xs text-foreground-muted">{t('promotion.action.bogo.offerTargetsHelp')}</p>
				</div>
				<OfferTargetRows
					title={t('promotion.action.bogo.offerProducts')}
					pickerLabel={t('promotion.action.bogo.product')}
					addLabel={t('promotion.action.bogo.addOfferProduct')}
					rows={value.offerProducts}
					onChange={offerProducts => onChange({ ...value, offerProducts })}
					showMaxQty
					options={[]}
					loadOptions={loadMockProducts}
					percentagePlaceholder={percentagePlaceholder}
				/>
				<OfferTargetRows
					title={t('promotion.action.bogo.offerCategories')}
					pickerLabel={t('promotion.action.bogo.category')}
					addLabel={t('promotion.action.bogo.addOfferCategory')}
					rows={value.offerCategories}
					onChange={offerCategories => onChange({ ...value, offerCategories })}
					showMaxQty={false}
					options={MOCK_CATEGORIES}
					percentagePlaceholder={percentagePlaceholder}
				/>
			</div>
		</div>
	);
}
